import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import {
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import moment from 'moment';
import UserProfileModal from '../components/UserProfileModal';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import AppDownloadShortcutCard from '../components/AppDownloadShortcutCard';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // User profile modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Request management state
  const [pendingCheckoutRequests, setPendingCheckoutRequests] = useState([]);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [selectedCheckoutRequest, setSelectedCheckoutRequest] = useState(null);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [activeRequestTab, setActiveRequestTab] = useState('checkout');
  const [selectedCheckoutIds, setSelectedCheckoutIds] = useState([]);
  const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);

  // Confirmation dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });

  useEffect(() => {
    loadDashboardStats();
    loadPendingRequests();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsProfileModalOpen(true);
  };

  const handleProfileModalClose = () => {
    setIsProfileModalOpen(false);
    setSelectedUser(null);
  };

  const loadPendingRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Load pending checkout requests
      const checkoutResponse = await axios.get('/api/checkout-requests/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCheckoutRequests(checkoutResponse.data.requests || []);

      // Load pending leave requests
      const leaveResponse = await axios.get('/api/leave-requests/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingLeaveRequests(leaveResponse.data.requests || []);
    } catch (err) {
      console.error('Failed to load pending requests:', err);
    }
  };

  // --- Bulk approval (multi-select) helpers ---
  const toggleCheckoutSelect = (id) => {
    setSelectedCheckoutIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllCheckouts = () => {
    setSelectedCheckoutIds((prev) => (prev.length === pendingCheckoutRequests.length ? [] : pendingCheckoutRequests.map((r) => r.id)));
  };
  const toggleLeaveSelect = (id) => {
    setSelectedLeaveIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllLeaves = () => {
    setSelectedLeaveIds((prev) => (prev.length === pendingLeaveRequests.length ? [] : pendingLeaveRequests.map((r) => r.id)));
  };

  const bulkApproveRequests = async (endpoint, ids) => {
    const token = localStorage.getItem('token');
    const { data } = await axios.post(
      endpoint,
      { requestIds: ids, notes: reviewNotes, ccEmails: [] },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (data.failed > 0) {
      toast.warning(`${data.message} (${data.failed} could not be approved.)`);
    } else {
      toast.success(data.message);
    }
  };

  const handleBulkApproveCheckout = () => {
    if (selectedCheckoutIds.length === 0) return;
    setConfirmConfig({
      title: 'Approve Checkout Requests',
      message: `Approve ${selectedCheckoutIds.length} selected checkout request(s)?`,
      confirmText: 'Approve',
      type: 'success',
      onConfirm: async () => {
        try {
          setProcessing(true);
          await bulkApproveRequests('/api/checkout-requests/approve-bulk', [...selectedCheckoutIds]);
          setSelectedCheckoutIds([]);
          setReviewNotes('');
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to approve requests');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleBulkApproveLeave = () => {
    if (selectedLeaveIds.length === 0) return;
    setConfirmConfig({
      title: 'Approve Leave Requests',
      message: `Approve ${selectedLeaveIds.length} selected leave request(s)?`,
      confirmText: 'Approve',
      type: 'success',
      onConfirm: async () => {
        try {
          setProcessing(true);
          await bulkApproveRequests('/api/leave-requests/approve-bulk', [...selectedLeaveIds]);
          setSelectedLeaveIds([]);
          setReviewNotes('');
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to approve leaves');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleApproveCheckout = async (requestId) => {
    const request = pendingCheckoutRequests.find(r => r.id === requestId);
    
    setConfirmConfig({
      title: 'Approve Checkout Request',
      message: `Approve checkout request for ${request?.user?.firstName} ${request?.user?.lastName}?`,
      confirmText: 'Approve',
      type: 'success',
      onConfirm: async () => {
        try {
          setProcessing(true);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/checkout-requests/approve/${requestId}`,
            { notes: reviewNotes, ccEmails: [] },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          toast.success('Checkout request approved successfully!');
          setReviewNotes('');
          setSelectedCheckoutRequest(null);
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to approve request');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleRejectCheckout = async (requestId) => {
    if (!reviewNotes.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    
    const request = pendingCheckoutRequests.find(r => r.id === requestId);
    
    setConfirmConfig({
      title: 'Reject Checkout Request',
      message: `Reject checkout request for ${request?.user?.firstName} ${request?.user?.lastName}?`,
      confirmText: 'Reject',
      type: 'danger',
      onConfirm: async () => {
        try {
          setProcessing(true);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/checkout-requests/reject/${requestId}`,
            { notes: reviewNotes },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          toast.success('Checkout request rejected.');
          setReviewNotes('');
          setSelectedCheckoutRequest(null);
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject request');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleApproveLeave = async (requestId) => {
    const request = pendingLeaveRequests.find(r => r.id === requestId);
    
    setConfirmConfig({
      title: 'Approve Leave Request',
      message: `Approve leave request for ${request?.user?.firstName} ${request?.user?.lastName}?`,
      confirmText: 'Approve',
      type: 'success',
      onConfirm: async () => {
        try {
          setProcessing(true);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/leave-requests/approve/${requestId}`,
            { notes: reviewNotes, ccEmails: [] },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          toast.success('Leave request approved successfully!');
          setReviewNotes('');
          setSelectedLeaveRequest(null);
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to approve leave');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleRejectLeave = async (requestId) => {
    if (!reviewNotes.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    
    const request = pendingLeaveRequests.find(r => r.id === requestId);
    
    setConfirmConfig({
      title: 'Reject Leave Request',
      message: `Reject leave request for ${request?.user?.firstName} ${request?.user?.lastName}?`,
      confirmText: 'Reject',
      type: 'danger',
      onConfirm: async () => {
        try {
          setProcessing(true);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/leave-requests/reject/${requestId}`,
            { notes: reviewNotes },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          toast.success('Leave request rejected.');
          setReviewNotes('');
          setSelectedLeaveRequest(null);
          loadPendingRequests();
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject leave');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error loading dashboard: {error}</div>
        <button
          onClick={loadDashboardStats}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Total Active Users',
      value: stats?.totalUsers || 0,
      icon: UserGroupIcon,
      color: 'bg-blue-500',
    },
    {
      name: "Today's Attendance",
      value: stats?.todayAttendance || 0,
      icon: ClockIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Monthly Hours',
      value: `${stats?.monthlyHours || 0}h`,
      icon: CalendarDaysIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Pending Requests',
      value: (pendingCheckoutRequests.length + pendingLeaveRequests.length) || 0,
      icon: BellIcon,
      color: 'bg-orange-500',
    },
  ];

  const cardColors = {
    'Total Active Users': { from: 'from-blue-50', to: 'to-blue-100', border: 'border-blue-200', gradient: 'from-blue-500 to-blue-600', text: 'text-blue-700', hover: 'hover:border-blue-400' },
    "Today's Attendance": { from: 'from-green-50', to: 'to-green-100', border: 'border-green-200', gradient: 'from-green-500 to-green-600', text: 'text-green-700', hover: 'hover:border-green-400' },
    'Monthly Hours': { from: 'from-yellow-50', to: 'to-yellow-100', border: 'border-yellow-200', gradient: 'from-yellow-500 to-yellow-600', text: 'text-yellow-700', hover: 'hover:border-yellow-400' },
    'Pending Requests': { from: 'from-orange-50', to: 'to-orange-100', border: 'border-orange-200', gradient: 'from-orange-500 to-orange-600', text: 'text-orange-700', hover: 'hover:border-orange-400' }
  };

  return (
    <div className="space-y-6">
      {/* Header - Admin Overview */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 rounded-3xl shadow-xl p-6 sm:p-7 lg:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight drop-shadow">
              Super Admin Overview
            </h1>
            <p className="text-white/85 text-sm sm:text-base max-w-xl">
              Monitor organization-wide attendance, requests, and department health from a single control center.
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="hidden sm:flex flex-col text-right text-xs text-white/80">
              <span className="font-semibold inline-flex items-center justify-end gap-1">
                Super Admin
              </span>
              <span>CoachLMS EMS</span>
            </div>
            <div className="h-14 w-14 sm:h-16 sm:w-16 lg:h-20 lg:w-20 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center border border-white/40 shadow-2xl ring-4 ring-white/10">
              <UserGroupIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Refreshed */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {statCards.map((card) => {
          const colors = cardColors[card.name];
          return (
            <div 
              key={card.name} 
              className={`group relative bg-gradient-to-br ${colors.from} ${colors.to} rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border ${colors.border} ${colors.hover} ${
                card.name === 'Pending Requests' && card.value > 0 ? 'cursor-pointer' : ''
              }`}
              onClick={() => {
                if (card.name === 'Pending Requests' && card.value > 0) {
                  setShowRequestsModal(true);
                }
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative p-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className={`flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br ${card.color.replace('bg-', 'from-')} ${card.color.replace('bg-', 'to-').replace('500', '600')} rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <card.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  {card.name === 'Pending Requests' && card.value > 0 && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-orange-600 text-white animate-pulse">
                      New requests
                    </span>
                  )}
                </div>
                <dt className={`text-xs sm:text-sm font-medium ${colors.text.replace('700', '900')} mb-1`}>
                  {card.name}
                </dt>
                <dd className={`text-2xl sm:text-3xl font-bold ${colors.text}`}>
                  {card.value}
                </dd>
                {card.name === 'Pending Requests' && card.value > 0 && (
                  <p className="mt-2 text-xs text-orange-600 font-medium">Tap to review pending approvals</p>
                )}
              </div>
            </div>
            );
        })}
        <AppDownloadShortcutCard />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        {/* Department Attendance Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Department Attendance Today</h3>
          {stats?.departmentStats?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.departmentStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No attendance data for today
            </div>
          )}
        </div>

        {/* Department Distribution Pie Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Department Distribution</h3>
          {stats?.departmentStats?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.departmentStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ department, count }) => `${department}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {stats.departmentStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No department data available
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow-sm border border-slate-200 rounded-2xl overflow-x-auto">
        <div className="px-4 py-5 sm:p-6 min-w-full">
          <h3 className="text-base sm:text-lg leading-6 font-semibold text-slate-900 mb-4">Recent Attendance Logs</h3>
          {stats?.recentLogs?.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Device
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.recentLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">
                          {log.user ? (
                            <button
                              onClick={() => handleUserClick(log.user)}
                              className="text-primary-600 hover:text-primary-800 hover:underline focus:outline-none"
                            >
                              {`${log.user.firstName} ${log.user.lastName}`}
                            </button>
                          ) : (
                            <span className="text-gray-900">Unknown</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {log.user?.officialEmail}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {moment(log.logTime).format('MMM DD, YYYY HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          log.punchLogType === 'CheckIn' || log.punchLogType === 'Check In'
                            ? 'bg-green-100 text-green-800' 
                            : (log.punchLogType === 'CheckOut' || log.punchLogType === 'Check Out'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800')
                        }`}>
                          {log.punchLogType || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.deviceNumber || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
              <p className="mt-1 text-sm text-gray-500">
                Attendance logs will appear here as they are recorded.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Requests Modal */}
      {showRequestsModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRequestsModal(false)}></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Pending Requests Management
                  </h3>
                  <button
                    onClick={() => setShowRequestsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setActiveRequestTab('checkout')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      activeRequestTab === 'checkout'
                        ? 'bg-orange-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Checkout Requests ({pendingCheckoutRequests.length})
                  </button>
                  <button
                    onClick={() => setActiveRequestTab('leave')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      activeRequestTab === 'leave'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Leave Requests ({pendingLeaveRequests.length})
                  </button>
                </div>

                {/* Checkout Requests Tab */}
                {activeRequestTab === 'checkout' && (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {pendingCheckoutRequests.length > 0 && (
                      <div className="flex flex-wrap items-center gap-3 pb-2 border-b border-gray-200">
                        <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                            checked={selectedCheckoutIds.length === pendingCheckoutRequests.length}
                            onChange={toggleSelectAllCheckouts}
                          />
                          Select all
                        </label>
                        {selectedCheckoutIds.length > 0 && (
                          <button
                            onClick={handleBulkApproveCheckout}
                            disabled={processing}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            ✓ Approve selected ({selectedCheckoutIds.length})
                          </button>
                        )}
                      </div>
                    )}
                    {pendingCheckoutRequests.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No pending checkout requests</p>
                    ) : (
                      pendingCheckoutRequests.map((request) => (
                  <div key={request.id} className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        checked={selectedCheckoutIds.includes(request.id)}
                        onChange={() => toggleCheckoutSelect(request.id)}
                      />
                      <span className="text-xs font-medium text-gray-600">Select</span>
                    </label>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          {request.user?.firstName} {request.user?.lastName}
                        </h4>
                        <p className="text-xs text-gray-600">{request.user?.officialEmail}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full">
                        Pending
                      </span>
                    </div>
                    <div className="text-xs text-gray-700 space-y-1 mb-2">
                      <div><strong>Date:</strong> {moment(request.requestDate).format('MMM DD, YYYY')}</div>
                      <div><strong>Time:</strong> {moment(request.requestedCheckoutTime).format('hh:mm A')}</div>
                      {request.reason && <div><strong>Reason:</strong> {request.reason}</div>}
                    </div>
                    
                    {selectedCheckoutRequest === request.id && (
                      <div className="mb-2">
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          rows={2}
                          className="w-full text-xs border-gray-300 rounded-md"
                          placeholder="Add notes..."
                        />
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      {selectedCheckoutRequest === request.id ? (
                        <>
                          <button
                            onClick={() => handleApproveCheckout(request.id)}
                            disabled={processing}
                            className="flex-1 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleRejectCheckout(request.id)}
                            disabled={processing}
                            className="flex-1 px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            ✗ Reject
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCheckoutRequest(null);
                              setReviewNotes('');
                            }}
                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setSelectedCheckoutRequest(request.id)}
                          className="w-full px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  </div>
                      ))
                    )}
                  </div>
                )}

                {/* Leave Requests Tab */}
                {activeRequestTab === 'leave' && (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {pendingLeaveRequests.length > 0 && (
                      <div className="flex flex-wrap items-center gap-3 pb-2 border-b border-gray-200">
                        <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            checked={selectedLeaveIds.length === pendingLeaveRequests.length}
                            onChange={toggleSelectAllLeaves}
                          />
                          Select all
                        </label>
                        {selectedLeaveIds.length > 0 && (
                          <button
                            onClick={handleBulkApproveLeave}
                            disabled={processing}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            ✓ Approve selected ({selectedLeaveIds.length})
                          </button>
                        )}
                      </div>
                    )}
                    {pendingLeaveRequests.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No pending leave requests</p>
                    ) : (
                      pendingLeaveRequests.map((request) => (
                  <div key={request.id} className="border border-purple-200 rounded-lg p-3 bg-purple-50">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        checked={selectedLeaveIds.includes(request.id)}
                        onChange={() => toggleLeaveSelect(request.id)}
                      />
                      <span className="text-xs font-medium text-gray-600">Select</span>
                    </label>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          {request.user?.firstName} {request.user?.lastName}
                        </h4>
                        <p className="text-xs text-gray-600">{request.user?.officialEmail}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-semibold bg-purple-200 text-purple-800 rounded">
                        {request.leaveType}
                      </span>
                    </div>
                    <div className="text-xs text-gray-700 space-y-1 mb-2">
                      <div><strong>From:</strong> {moment(request.startDate).format('MMM DD, YYYY')}</div>
                      <div><strong>To:</strong> {moment(request.endDate).format('MMM DD, YYYY')}</div>
                      <div><strong>Days:</strong> {request.totalDays} working day(s)</div>
                      {request.reason && <div><strong>Reason:</strong> {request.reason}</div>}
                    </div>
                    
                    {selectedLeaveRequest === request.id && (
                      <div className="mb-2">
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          rows={2}
                          className="w-full text-xs border-gray-300 rounded-md"
                          placeholder="Add notes..."
                        />
                      </div>
                    )}
                    
                    <div className="flex space-x-2">
                      {selectedLeaveRequest === request.id ? (
                        <>
                          <button
                            onClick={() => handleApproveLeave(request.id)}
                            disabled={processing}
                            className="flex-1 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => handleRejectLeave(request.id)}
                            disabled={processing}
                            className="flex-1 px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            ✗ Reject
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLeaveRequest(null);
                              setReviewNotes('');
                            }}
                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setSelectedLeaveRequest(request.id)}
                          className="w-full px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                        >
                          Review
                        </button>
                      )}
                    </div>
                  </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={handleProfileModalClose}
        user={selectedUser}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText || 'Confirm'}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
      />
    </div>
  );
}

export default Dashboard;
